import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getMyVouchers, validateVoucher, isApiSuccess } from "../../services/api";

const MAX_PAGES = 10;
// amount the voucher takes off the bill
const voucherValue = (x) => Number(x?.voucher_price ?? x?.voucher?.voucher_price) || 0;

// All of the customer's issued vouchers (every page of /voucher/my-vouchers)
export const fetchStoreVouchers = createAsyncThunk("storeVoucher/fetch", async (_, { rejectWithValue }) => {
    try {
        const first = await getMyVouchers(1);
        let items = first?.data ?? [];
        const last = Math.min(first?.meta?.last_page ?? 1, MAX_PAGES);
        for (let p = 2; p <= last; p++) {
            const res = await getMyVouchers(p);
            items = items.concat(res?.data ?? []);
        }
        return items;
    } catch (e) {
        return rejectWithValue(e?.message || "Unable to load your vouchers.");
    }
});

// Validate a voucher code for this store, then mark it as applied
export const applyVoucher = createAsyncThunk(
    "storeVoucher/apply",
    async ({ code,online, storeId, billAmount, name }, { rejectWithValue, getState }) => {
        const voucherCode = String(code || "").trim();
        if (!voucherCode) return rejectWithValue("Enter a voucher code.");
        try {
            const res = await validateVoucher({ voucher_code: voucherCode, store_id: parseInt(storeId, 10),online });
            if (!isApiSuccess(res)) return rejectWithValue(res?.message || "Invalid voucher code.");

            const d = res?.data ?? {};
            const owned = getState().storeVoucher.list.find((v) => v.voucher_code === voucherCode);
            const value = voucherValue(d) || voucherValue(owned);
            const min = Number(d?.min_purchase_value ?? d?.voucher?.min_purchase_value ?? owned?.voucher?.min_purchase_value ?? 0);

            if (min > 0 && Number(billAmount) < min) {
                return rejectWithValue(`Minimum bill of ₹${min} required for this voucher.`);
            }
            if (value > 0 && Number(billAmount) < value) {
                return rejectWithValue(`Can't use this voucher: bill value must be at least ₹${value}.`);
            }
            return {
                code: voucherCode,
                name: name ?? d?.voucher_name ?? d?.voucher?.voucher_name ?? owned?.voucher_name ?? null,
                value,
            };
        } catch (e) {
            return rejectWithValue(e?.message || "Invalid voucher code.");
        }
    }
);

const initialState = {
    list: [],
    listStatus: "idle", // idle | loading | succeeded | failed
    listError: null,
    applied: null, // { code, name }
    applying: false,
    applyError: null,
};

const storeVoucherSlice = createSlice({
    name: "storeVoucher",
    initialState,
    reducers: {
        clearApplied(state) {
            state.applied = null;
            state.applyError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchStoreVouchers.pending, (s) => {
                s.listStatus = "loading";
                s.listError = null;
            })
            .addCase(fetchStoreVouchers.fulfilled, (s, a) => {
                s.listStatus = "succeeded";
                s.list = a.payload;
            })
            .addCase(fetchStoreVouchers.rejected, (s, a) => {
                s.listStatus = "failed";
                s.listError = a.payload || "Unable to load your vouchers.";
            })
            .addCase(applyVoucher.pending, (s) => {
                s.applying = true;
                s.applyError = null;
            })
            .addCase(applyVoucher.fulfilled, (s, a) => {
                s.applying = false;
                s.applied = a.payload;
            })
            .addCase(applyVoucher.rejected, (s, a) => {
                s.applying = false;
                s.applyError = a.payload || "Invalid voucher code.";
            });
    },
});

export const { clearApplied } = storeVoucherSlice.actions;
export default storeVoucherSlice.reducer;